import type { Deck } from '~~/entities/deck'
import { useToast } from '~/components/ui/toast'
import type { Slide } from '~~/entities/slide'
import { SlideTypes } from '~~/entities/slide'

export const PASSWORD_REQUIRED_HEADER_MSG = 'SCRAM-SHA-512 realm="Access to private deck with password"'

export class LegacyDeckError extends Error {
  constructor(message: string = 'This 3D Presentation contains legacy 3D model slides that are no longer supported.') {
    super(message)
    this.name = 'LegacyDeckError'
  }
}

const INCLUDE = ['company', 'site_image', 'model_camera', 'assets', 'able_to_book', 'inactive_meeting_slides']

export function useEditDeck({ id, onSetActiveSlide }: { id: string; onSetActiveSlide?: (slide: Slide) => void }) {
  const { $analytics } = useNuxtApp()
  const { getDeckById } = useDecks()

  const { toast } = useToast()

  const state = reactive<{
    deck: Deck | null
    activeSlide: Slide | null
  }>({
    deck: null,
    activeSlide: null,
  })

  const deckModeState = useDeckMode()

  const fetchDeck = async () => {
    try {
      const res = await getDeckById(id, {
        include: INCLUDE,
      })

      const hasV2Models = res.slides?.some(s => s.type === SlideTypes.Model)
      const hasSiteSlides = res.slides?.some(s => s.type === SlideTypes.Site)
      const hasV3Models = res.slides?.some(s => s.type === SlideTypes.Scene)
      if ((hasV2Models || hasSiteSlides) && !hasV3Models) {
        throw new LegacyDeckError()
      }

      if (res) state.deck = res
    } catch (e) {
      if (e instanceof LegacyDeckError) {
        throw e
      }

      await handleEditPermissionError()
    }
  }

  async function handleEditPermissionError() {
    state.deck = await getDeckById(id, {
      include: INCLUDE,
    })
  }

  const updateDeck = async (update: Partial<Deck>) => {
    if (!state.deck?.company_id) {
      throw new Error('Cannot update deck: company_id is required')
    }

    const { updateCompanyDeck } = useCompanyDecks(state.deck.company_id)
    await updateCompanyDeck(id, update)
    await fetchDeck()
    toast({
      title: '3D Presentation Updated',
      description: '3D Presentation was successfully updated',
      variant: 'success',
      duration: 4000,
    })
  }

  const setInitialSlide = (slideId?: number) => {
    if (slideId) {
      setActiveSlide(slideId)
    } else {
      setActiveSlide(state.deck?.slides?.[0]?.id as number)
    }
  }

  const setActiveSlide = (slideId: number) => {
    state.activeSlide = state.deck?.slides?.find(slide => slide.id === slideId) ?? null

    if (state.activeSlide) {
      onSetActiveSlide?.(state.activeSlide)
    }
  }

  const viewableSlides = computed(() => {
    return state.deck?.slides?.filter(slide => slide.type !== SlideTypes.Header)
  })

  const goToNextSlide = () => {
    if (!state.activeSlide || !viewableSlides.value?.length) return

    const currentIndex = viewableSlides.value.findIndex(slide => slide.id === state.activeSlide?.id)

    if (currentIndex === -1) return

    const nextSlide = viewableSlides.value?.[currentIndex + 1]
    if (nextSlide) {
      setActiveSlide(nextSlide.id)
    }
  }

  const goToPreviousSlide = () => {
    if (!state.activeSlide || !viewableSlides.value?.length) return

    const currentIndex = viewableSlides.value.findIndex(slide => slide.id === state.activeSlide?.id)

    if (currentIndex === -1) return

    const previousSlide = viewableSlides.value?.[currentIndex - 1]
    if (previousSlide) {
      setActiveSlide(previousSlide.id)
    }
  }

  const clearActiveSlide = () => {
    state.activeSlide = null
  }

  return {
    ...toRefs(state),
    ...deckModeState,
    fetchDeck,
    updateDeck,
    viewableSlides,
    setActiveSlide,
    setInitialSlide,
    clearActiveSlide,
    goToNextSlide,
    goToPreviousSlide,
  }
}
